import React from "react";
import { Link, Navigate } from "react-router-dom";
import { GraduationCap, CameraPlus, Sparkle, EnvelopeSimple, TrendUp, CheckCircle, Lightbulb, Users, Target, ArrowRight, Lock, ChartLine, ChatCircleText, Warning, Clock } from "@phosphor-icons/react";

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`w-full ${className}`}>
    <div className="max-w-5xl mx-auto px-5 sm:px-8">{children}</div>
  </section>
);

const H2 = ({ children }) => (
  <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl leading-tight tracking-tight">{children}</h2>
);

const Card = ({ title, children, className = "" }) => (
  <div className={`bg-white rounded-3xl p-5 shadow-sm border border-black/5 ${className}`}>
    <div className="font-outfit font-bold text-brand-ink text-base mb-2">{title}</div>
    <div className="font-nunito text-sm text-brand-ink2 leading-relaxed">{children}</div>
  </div>
);

export default function Landing() {
  const token = localStorage.getItem("evodevi_token");
  const role = localStorage.getItem("evodevi_role") || "student";
  if (token) return <Navigate to={role === "parent" ? "/parent" : "/app"} replace />;

  return (
    <div className="bg-brand-paper min-h-screen text-brand-ink font-nunito">
      {/* NAV */}
      <nav className="sticky top-0 z-30 bg-brand-paper/85 backdrop-blur border-b border-black/5">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-xl bg-brand-primary flex items-center justify-center">
              <GraduationCap size={20} weight="duotone" color="#1D3557" />
            </div>
            <span className="font-outfit font-extrabold text-lg text-brand-ink">Ödevix</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 ml-6 font-nunito font-semibold text-sm text-brand-ink2">
            <a href="#nasil" data-testid="nav-nasil">Nasıl çalışır</a>
            <a href="#kimler" data-testid="nav-kimler">Kimler için</a>
            <a href="#hakkimizda" data-testid="nav-hakkimizda">Hakkımızda</a>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex px-3 py-2 font-nunito font-bold text-sm text-brand-ink">Giriş yap</Link>
            <Link to="/register" data-testid="nav-register" className="inline-flex bg-brand-primary text-brand-ink px-4 py-2.5 rounded-full font-outfit font-bold text-sm shadow-[0_4px_18px_rgba(255,183,3,0.35)]">Kayıt ol</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <Section className="pt-10 pb-16">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block bg-brand-ink text-brand-primary font-outfit font-bold text-[11px] tracking-widest px-3 py-1.5 rounded-full mb-5">ORTAOKUL VELİLERİ İÇİN</span>
            <h1 className="font-outfit font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-ink">
              Ödevix ile eksik konularını anında öğren ve pekiştir.
            </h1>
            <p className="mt-5 text-lg text-brand-ink2 leading-relaxed">
              Çocuğunuz ödevinin fotoğrafını çeker. Ödevix boş bırakılan ve yanlış yapılan soruları analiz eder, eksik konuyu bulur ve raporu size e-posta ile gönderir. Sorun büyümeden, siz görürsünüz.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="bg-brand-primary text-brand-ink font-outfit font-bold text-base px-6 py-3.5 rounded-full shadow-[0_6px_22px_rgba(255,183,3,0.45)] inline-flex items-center gap-2">
                Kayıt ol <ArrowRight size={18} weight="bold" />
              </Link>
              <a href="#nasil" data-testid="hero-cta-nasil" className="bg-white text-brand-ink font-outfit font-bold text-base px-6 py-3.5 rounded-full border border-black/10">Nasıl çalışır?</a>
            </div>
            <div className="mt-5 text-sm text-brand-ink2 flex flex-wrap gap-x-4 gap-y-1">
              <span>· 8 ders, Almanca-Fransızca-İtalyanca dahil</span>
              <span>· Cevaplar çocuğa değil, veliye gider</span>
              <span>· Uygulama indirmeye gerek yok</span>
            </div>
          </div>

          {/* Sample report card */}
          <div className="relative">
            <div className="absolute -inset-4 bg-brand-primary/25 rounded-[36px] blur-2xl -z-0"></div>
            <div className="relative bg-white rounded-3xl shadow-xl border border-black/5 p-6" data-testid="hero-sample-report">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-2xl bg-[#E0F2FE] flex items-center justify-center">
                  <ChartLine size={20} weight="duotone" color="#0284C7" />
                </div>
                <div>
                  <div className="font-outfit font-extrabold text-brand-ink">Matematik</div>
                  <div className="text-xs text-brand-ink2 font-nunito">Kesirler ödevi</div>
                </div>
              </div>
              <div className="bg-brand-paperAlt rounded-2xl p-3 mb-3 font-nunito text-sm text-brand-ink">
                <b>12 sorudan 4'ü boş bırakılmış, 2'si yanlış.</b>
              </div>
              <div className="flex flex-col gap-2 mb-3">
                <div className="rounded-2xl bg-bad-bg p-3 text-sm font-nunito">
                  <div className="font-bold text-bad">Eksik konu</div>
                  Kesirlerde toplama (paydalar farklı)
                </div>
                <div className="rounded-2xl bg-good-bg p-3 text-sm font-nunito">
                  <div className="font-bold text-good">Güçlü</div>
                  Kesir sadeleştirme
                </div>
              </div>
              <div className="flex items-center gap-2 text-brand-ink2 font-nunito text-xs">
                <Lock size={14} weight="duotone" /> Çözümler onayınızı bekliyor
              </div>
            </div>
            <div className="text-xs text-brand-ink2 mt-3 text-center italic">Rapor içeriği temsilidir.</div>
          </div>
        </div>
      </Section>

      {/* SORUN */}
      <Section className="py-16 bg-brand-ink text-white rounded-none">
        <div className="max-w-4xl mx-auto text-white text-center px-4">
          <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl leading-tight tracking-tight text-white">
            Ödevi imzalıyorsunuz. Peki boş bırakılan soruları kim görüyor?
          </h2>
          <p className="mt-5 text-white/85 leading-relaxed text-lg">
            Okulda velinin görevi ders çalıştırmak değil, ödevi imzalamak. Ama 25 kişilik sınıfta öğretmen her boş soruya tek tek bakamıyor. "Öğretmenimle bakacağız" denilen sorular birikiyor ve dönem sonunda eksik konu olarak karşınıza çıkıyor.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {[
            {icon: Warning, t: "Karne notu yüksek, yazılı notu düşük", d: "Sözlü ve dinleme notları ortalamayı taşıyor; asıl eksik görünmüyor."},
            {icon: Clock, t: "Eksik, geçmiş sınıftan geliyor", d: "7. sınıfta test çözerken 6. sınıf konularının açığı ortaya çıkıyor."},
            {icon: Lightbulb, t: "Geç öğrenince müdahale zorlaşıyor", d: "Öğretmeni aramak, özel ders ya da konu anlatımı videosu izletmek — hepsi zamanında bilmeye bağlı."},
          ].map((c, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-3xl p-5">
              <c.icon size={26} weight="duotone" color="#FFB703" />
              <div className="font-outfit font-bold text-white mt-3 mb-2">{c.t}</div>
              <div className="font-nunito text-sm text-white/75 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* NASIL ÇALIŞIR */}
      <Section id="nasil" className="py-20">
        <H2>3 Adım. Öğrenci fotoğraflar, Ödevix analiz eder, rapor size gelir.</H2>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {[
            {n: 1, icon: CameraPlus, t: "Ödevin fotoğrafını çek", d: "Çocuğunuz bitirdiği (ya da bitiremediği) ödevin fotoğrafını çeker. Birden fazla sayfa, tek seferde. Almanca, Fransızca ve İtalyanca dahil 8 ders."},
            {n: 2, icon: Sparkle, t: "Ödevix analiz eder", d: "Yapay zekâ boş bırakılan ve yanlış yapılan soruları bulur, hangi konudan kaynaklandığını belirler ve o konuya özel 10 soruluk pratik test hazırlar."},
            {n: 3, icon: EnvelopeSimple, t: "Rapor velinin e-postasına gelir", d: "Eksik konu, güçlü yanlar ve çözümler size gelir. Çözümleri çocuğunuza ne zaman açacağınıza siz karar verirsiniz. Her hafta bir de özet rapor."},
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-3xl p-6 shadow-sm border border-black/5 relative">
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-2xl bg-brand-primary flex items-center justify-center font-outfit font-extrabold text-brand-ink text-lg shadow-md">{s.n}</div>
              <s.icon size={30} weight="duotone" color="#1D3557" />
              <div className="font-outfit font-bold text-brand-ink text-lg mt-4 mb-2">{s.t}</div>
              <div className="font-nunito text-sm text-brand-ink2 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* EN BÜYÜK FAYDA */}
      <Section className="py-20 bg-brand-paperAlt/60">
        <div className="max-w-3xl">
          <H2>Erken tanı, öğrenimi kurtarır.</H2>
          <p className="mt-5 text-lg text-brand-ink2 leading-relaxed">
            Eksiği küçükken yakalarsınız — dönem sonunda değil, ödev bittiği gün. Konu bir kez tespit edildiğinde ne yapacağınız size kalmış: öğretmeniyle konuşmak, kısa bir özel ders ya da o akşam birlikte konu anlatımı videosu izlemek. Ödevix karar vermez; zamanında görmenizi sağlar.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-10">
          <Card title="Kopya aracı değil">Çözümler önce veliye gider. Çocuk cevabı değil, konuyu öğrenir.</Card>
          <Card title="Kendi ödevinden test">Genel bir soru bankası değil; o gün eksik çıkan konudan 10 soru.</Card>
          <Card title="Haftalık özet">Hangi derste hangi konu tekrar ediyor, her hafta tek e-postada.</Card>
          <Card title="Çocuk için motivasyon">Puan, rozet ve görevler. Ödev kontrolü çocuk için ceza değil, oyun.</Card>
        </div>
      </Section>

      {/* KİMLER İÇİN */}
      <Section id="kimler" className="py-20 bg-brand-primary/20">
        <div className="max-w-3xl">
          <H2>Ödev takibini istemeyen değil, yetişemeyen veliler için.</H2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {[
            {icon: Users, t: "Ortaokul velileri", d: "Okulda ödev imzalayan ama her soruya bakacak zamanı olmayan anne-babalar."},
            {icon: ChatCircleText, t: "\"Ödev nasıldı?\" yerine", d: "Akşam eve geldiğinde 'ödev nasıldı?' sorusuna gerçek bir cevap isteyenler."},
            {icon: Target, t: "Yabancı dil ödevleri", d: "Almanca ya da İngilizce ödevini kontrol edemeyen veliler için de çalışır."},
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-3xl p-5 shadow-sm border border-black/5">
              <c.icon size={26} weight="duotone" color="#1D3557" />
              <div className="font-outfit font-bold text-brand-ink mt-3 mb-2">{c.t}</div>
              <div className="font-nunito text-sm text-brand-ink2 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* HAKKIMIZDA */}
      <Section id="hakkimizda" className="py-20">
        <div className="grid md:grid-cols-5 gap-8 items-start">
          <div className="md:col-span-2">
            <div className="aspect-square bg-brand-paperAlt rounded-3xl border-2 border-dashed border-brand-ink2/30 flex items-center justify-center text-brand-ink2/60 font-nunito text-sm" data-testid="about-photo-placeholder">
              Fotoğraf alanı<br />(sonra eklenecek)
            </div>
          </div>
          <div className="md:col-span-3">
            <H2>Bu fikir bir velinin mutfak masasında doğdu.</H2>
            <div className="mt-5 text-brand-ink2 leading-relaxed space-y-4 text-base">
              <p>Ben Sevgi. Kızım İstanbul'da bir okulda 7. sınıfta okuyor. Velilerden beklenen tek şey ödevi imzalamak; ders çalıştırmak ya da kontrol etmek değil.</p>
              <p>İmzalarken fark ettim: bazı sorular boş kalıyordu. Sorduğumda cevap hep aynıydı — "okulda öğretmenimle bakacağız." 25 kişilik bir sınıfta bunun olmadığını biliyordum. Boş sorular birikiyor, eksik konuya dönüşüyordu.</p>
              <p>Karneye baksam her şey yolundaydı: Almanca 5, Matematik 5. Ama Almanca yazılısı 55'ti; ortalamayı sözlü ve dinleme notları taşıyordu. Matematikte test çözdürünce 6. sınıftan kalan eksik konular tek tek çıkıyordu.</p>
              <p>Eksiği zamanında bilseydim öğretmenini arar, kısa bir özel ders aldırır ya da o konuda bir konu anlatımı videosu açıp birlikte izlerdik. Sorun küçükken çözmek, dönem sonunda telafi etmekten çok daha kolay.</p>
              <p className="font-outfit font-extrabold text-brand-ink text-lg">Ödevix'i bunun için yaptım: velinin ödevi imzalarken göremediğini, o gün görmesi için.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-20 bg-brand-primary">
        <div className="max-w-3xl text-center mx-auto">
          <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl text-brand-ink tracking-tight">Bir sonraki ödevde deneyin.</h2>
          <p className="mt-4 text-brand-ink text-lg leading-relaxed">Kayıt olun, çocuğunuzun profilini açın, ilk fotoğrafı yükleyin. Rapor e-postanıza gelsin.</p>
          <Link to="/register" data-testid="cta-final-register" className="mt-7 inline-flex bg-brand-ink text-brand-primary font-outfit font-bold text-lg px-8 py-4 rounded-full shadow-lg hover:-translate-y-0.5 transition-transform">
            Kayıt ol <ArrowRight size={20} weight="bold" className="ml-2" />
          </Link>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="bg-brand-ink text-white py-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div>
            <div className="font-outfit font-extrabold text-xl">Ödevix</div>
            <div className="text-white/70 text-sm mt-1">Ödevini fotoğrafla, sonucu anında öğren.</div>
          </div>
          <div className="flex flex-wrap gap-5 font-nunito text-sm">
            <Link to="/login" className="text-white/80 hover:text-white" data-testid="footer-login">Giriş yap</Link>
            <a href="#" className="text-white/80 hover:text-white">Gizlilik ve KVKK</a>
            <a href="#" className="text-white/80 hover:text-white">İletişim</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
